<template>
	<div class="tvtime-importer" :class="{ 'tvtime-importer--compact': state !== 'idle' }">
		<template v-if="state === 'idle'">
			<div class="tvtime-importer__header">
				<h1 class="tvtime-importer__title">Bring your TV Time history to JustWatch</h1>
				<p class="tvtime-importer__subtitle">
					<template v-if="isLoggedIn">
						You are now logged in. Please re-upload your TV Time export so it's permanently tied to your
						JustWatch account.
					</template>
					<template v-else>
						TV Time is shutting down on July 15 but the shows and movies you've tracked over the years don't
						have to disappear. Upload your TV Time export and we'll bring your watch history and watchlist
						into JustWatch.
					</template>
				</p>
			</div>

			<div
				ref="dropzoneRef"
				class="tvtime-importer__dropzone"
				:class="{ 'tvtime-importer__dropzone--drag-over': isOverDropZone }"
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
					<path
						d="M53.9263 35.665C52.2703 35.665 50.9247 37.0106 50.9247 38.6666V47.2057H13.7151V38.6666C13.7151 37.0106 12.3696 35.665 10.7135 35.665C9.05746 35.665 7.71191 37.0106 7.71191 38.6666V50.1555C7.71191 51.8116 9.05746 53.1572 10.7135 53.1572H53.9263C55.5824 53.1572 56.9279 51.8116 56.9279 50.1555V38.6666C56.9279 37.0106 55.5824 35.665 53.9263 35.665Z"
						fill="#D2D3D5"
					/>
					<path
						d="M25.5664 24.2279L29.3443 20.45V39.3912C29.3443 41.0473 30.6899 42.3928 32.3459 42.3928C34.002 42.3928 35.3475 41.0473 35.3475 39.3912V20.3465L39.2289 24.2279C40.4192 25.4182 42.2823 25.4182 43.4726 24.2279C44.6629 23.0376 44.6629 21.1745 43.4726 19.9842L34.5712 11.0829C34.002 10.5136 33.2257 10.2031 32.4494 10.2031C31.6732 10.2031 30.8969 10.5136 30.3276 11.0829L21.4263 19.9842C20.236 21.1745 20.236 23.0376 21.4263 24.2279C22.5131 25.4182 24.3761 25.4182 25.5664 24.2279Z"
						fill="#D2D3D5"
					/>
				</svg>

				<input
					ref="fileInputRef"
					type="file"
					accept=".zip"
					class="tvtime-importer__file-input"
					@change="onFileChange"
				/>

				<BasicButton
					:color="selectedFile ? 'tertiary' : 'primary'"
					:blocked="true"
					size="large"
					class="tvtime-importer__upload-button"
					@click="fileInputRef?.click()"
				>
					{{ selectedFile ? 'Upload another list' : 'Upload your TV Time export' }}
				</BasicButton>

				<span class="tvtime-importer__hint">File supported .zip</span>

				<div v-if="selectedFile" class="tvtime-importer__file-chip">
					<span class="tvtime-importer__file-chip-name">{{ selectedFile.name }}</span>
					<button class="tvtime-importer__file-chip-remove" aria-label="Remove file" @click="clearFile">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path
								d="M12.7813 4.28123C13.0751 3.98748 13.0751 3.51248 12.7813 3.22185C12.4876 2.93123 12.0126 2.9281 11.722 3.22185L8.00322 6.9406L4.28135 3.21873C3.9876 2.92498 3.5126 2.92498 3.22197 3.21873C2.93135 3.51248 2.92822 3.98748 3.22197 4.2781L6.94072 7.99685L3.21885 11.7187C2.9251 12.0125 2.9251 12.4875 3.21885 12.7781C3.5126 13.0687 3.9876 13.0718 4.27822 12.7781L7.99697 9.05935L11.7188 12.7812C12.0126 13.075 12.4876 13.075 12.7782 12.7812C13.0688 12.4875 13.072 12.0125 12.7782 11.7219L9.05947 8.0031L12.7813 4.28123Z"
								fill="white"
							/>
						</svg>
					</button>
				</div>
			</div>

			<div class="tvtime-importer__cta">
				<BasicButton
					color="primary"
					size="large"
					:disabled="!selectedFile || isMatching || isImporting"
					@click="createNow"
				>
					{{ isImporting ? 'Importing…' : isMatching ? 'Matching titles…' : 'Import my history' }}
				</BasicButton>

				<p class="tvtime-importer__footer">
					Don't have your export yet? Download it from TV Time before July 15,
					<a
						href="https://gdpr.tvtime.com/gdpr/self-service"
						target="_blank"
						rel="noopener noreferrer"
						class="tvtime-importer__footer-link"
						>here</a
					>.
				</p>
			</div>
		</template>

		<template v-else-if="state === 'matched'">
			<div class="tvtime-importer__success-icon">
				<svg xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" fill="none">
					<path
						d="M40.3054 8.58335C41.0888 9.36668 41.0888 10.6333 40.3054 11.4083L18.3054 33.4167C17.5221 34.2 16.2554 34.2 15.4804 33.4167L4.13877 22.0833C3.35544 21.3 3.35544 20.0333 4.13877 19.2583C4.9221 18.4833 6.18877 18.475 6.96377 19.2583L16.8804 29.175L37.4721 8.58335C38.2554 7.80002 39.5221 7.80002 40.2971 8.58335H40.3054Z"
						fill="#339b2a"
					/>
				</svg>
			</div>
			<p class="tvtime-importer__success-message">{{ matchedCount }} Titles found and ready to import</p>
			<BasicButton color="primary" :blocked="true" size="large" @click="openSignup">
				Sign up to save your titles
			</BasicButton>
		</template>

		<template v-else-if="state === 'success'">
			<div class="tvtime-importer__success-icon">
				<svg xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" fill="none">
					<path
						d="M40.3054 8.58335C41.0888 9.36668 41.0888 10.6333 40.3054 11.4083L18.3054 33.4167C17.5221 34.2 16.2554 34.2 15.4804 33.4167L4.13877 22.0833C3.35544 21.3 3.35544 20.0333 4.13877 19.2583C4.9221 18.4833 6.18877 18.475 6.96377 19.2583L16.8804 29.175L37.4721 8.58335C38.2554 7.80002 39.5221 7.80002 40.2971 8.58335H40.3054Z"
						fill="#339b2a"
					/>
				</svg>
			</div>
			<p class="tvtime-importer__success-message">
				{{ importedCount }} Titles were successfully imported, and you can find them on your watch list
			</p>
			<BasicButton color="primary" :blocked="true" size="large" tag="div" @click="goToWatchlist">
				Go to my watchlist
			</BasicButton>
		</template>

		<template v-else-if="state === 'error'">
			<div class="tvtime-importer__error-icon">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="40"
					height="40"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<line x1="18" y1="6" x2="6" y2="18" />
					<line x1="6" y1="6" x2="18" y2="18" />
				</svg>
			</div>
			<p class="tvtime-importer__error-message">
				Something went wrong with your import. Please try again or reach out to us at
				<a href="mailto:feedback@justwatch.com" class="tvtime-importer__error-link">feedback@justwatch.com</a>
			</p>
			<BasicButton color="primary" size="large" @click="retry"> Try again </BasicButton>
		</template>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useDropZone } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import BasicButton from '@/ui-components/BasicButton.vue';
import { AccountModal } from '@/lazy-load-components';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import {
	ImportTvTimeDocument,
	type ImportTvTimeMutation,
	type ImportTvTimeMutationVariables,
} from '@/graphql/mutation/ImportTVTime.mutation';

type ImportState = 'idle' | 'matched' | 'success' | 'error';

const router = useRouter();
const { isLoggedIn } = storeToRefs(useUserStore());

const state = ref<ImportState>('idle');
const fileInputRef = ref<HTMLInputElement | null>(null);
const dropzoneRef = ref<HTMLElement | null>(null);
const selectedFile = ref<File | null>(null);

const { isOverDropZone } = useDropZone(dropzoneRef, {
	dataTypes: ['application/zip', 'application/x-zip-compressed'],
	onDrop(files) {
		const file = files?.[0] ?? null;
		if (!file) return;
		selectedFile.value = file;
		state.value = 'idle';
	},
});
const isMatching = ref(false);
const isImporting = ref(false);
const matchedCount = ref(0);
const importedCount = ref(0);

const { mutate } = useMutation<ImportTvTimeMutation, ImportTvTimeMutationVariables>(ImportTvTimeDocument, {
	throws: 'always',
});

function onFileChange(event: Event) {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0] ?? null;
	if (!file) return;
	selectedFile.value = file;
	state.value = 'idle';
}

function clearFile() {
	selectedFile.value = null;
	state.value = 'idle';
	if (fileInputRef.value) {
		fileInputRef.value.value = '';
	}
}

async function createNow() {
	if (!selectedFile.value) return;
	if (isLoggedIn.value) {
		await runImport();
	} else {
		await matchTitles();
	}
}

async function matchTitles() {
	isMatching.value = true;
	try {
		const result = await mutate({ file: selectedFile.value!, readOnly: true });
		const payload = result?.data?.importTVTimeLists;
		matchedCount.value = (payload?.watchlistMatched ?? 0) + (payload?.seenlistMatched ?? 0);
		state.value = 'matched';
	} catch {
		state.value = 'error';
	} finally {
		isMatching.value = false;
	}
}

async function runImport() {
	if (!selectedFile.value || isImporting.value) return;
	isImporting.value = true;
	try {
		const result = await mutate({ file: selectedFile.value, readOnly: false });
		const payload = result?.data?.importTVTimeLists;
		importedCount.value = (payload?.watchlistAdded ?? 0) + (payload?.seenlistAdded ?? 0);
		state.value = 'success';
	} catch {
		state.value = 'error';
	} finally {
		isImporting.value = false;
	}
}

function retry() {
	state.value = 'idle';
	selectedFile.value = null;
	matchedCount.value = 0;
	isMatching.value = false;
	isImporting.value = false;
	if (fileInputRef.value) {
		fileInputRef.value.value = '';
	}
}

function openSignup() {
	ModalHelper.openModal(AccountModal, { closable: true }, { cssClass: 'account-modal' }, 'TvtimeImporter');
}

watch(isLoggedIn, newVal => {
	if (newVal && state.value === 'matched') {
		runImport();
	}
});

function goToWatchlist() {
	router.push({ name: 'app.lists.my-lists' });
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

.tvtime-importer {
	background-color: #101720;
	border-radius: 12px;
	padding: 32px;
	max-width: 480px;
	min-height: 520px;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 68px;

	&--compact {
		gap: 24px;
	}

	&__header {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		width: 100%;
	}

	&__title {
		font-size: 20px;
		font-weight: 900;
		line-height: 24px;
		letter-spacing: 0.3px;
		color: $jw-color-white;
		text-align: center;
		margin: 0;
	}

	&__subtitle {
		font-size: 14px;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
		color: #909398;
		text-align: center;
		margin: 0;
	}

	&__dropzone {
		border: 2px dashed rgba($jw-color-white, 0.25);
		border-radius: 8px;
		padding: 28px 20px 20px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 14px;
		width: 100%;
		transition: border-color 0.15s ease, background-color 0.15s ease;

		&--drag-over {
			border-color: rgba($jw-color-white, 0.6);
			background-color: rgba($jw-color-white, 0.04);
		}
	}

	&__file-input {
		display: none;
	}

	&__upload-button.basic-button.lg {
		font-size: 14px;

		@include sm() {
			font-size: 13px;
			padding: 7.5px 15px;
		}
	}

	&__hint {
		font-size: 16px;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.24px;
		color: #d2d3d5;
	}

	&__file-chip {
		display: flex;
		align-items: center;
		gap: 8px;
		background-color: #232932;
		border-radius: 8px;
		padding: 6px 10px;
		width: 100%;
		justify-content: space-between;
	}

	&__file-chip-name {
		font-size: 16px;
		font-weight: 900;
		line-height: 24px;
		letter-spacing: 0.24px;
		color: #d2d3d5;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__file-chip-remove {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		color: $jw-color-mediumgray--light;
		display: flex;
		align-items: center;
		flex-shrink: 0;

		&:hover {
			color: $jw-color-white;
		}
	}

	&__success-icon {
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background-color: #00290b;
		display: flex;
		align-items: center;
		justify-content: center;
		color: $jw-color-white;
	}

	&__success-message {
		font-size: 16px;
		font-weight: 900;
		line-height: 24px;
		letter-spacing: 0.24px;
		color: $jw-color-white;
		text-align: center;
		margin: 0;
	}

	&__error-icon {
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background-color: $jw-color-red;
		display: flex;
		align-items: center;
		justify-content: center;
		color: $jw-color-white;
	}

	&__error-message {
		font-size: 15px;
		color: $jw-color-white;
		text-align: center;
		line-height: 1.6;
		margin: 0;
	}

	&__cta {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		width: 100%;
	}

	&__footer {
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
		color: #909398;
		text-align: center;
		margin: 0;
	}

	&__footer-link {
		color: $jw-color-gold;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	&__error-link {
		color: $jw-color-gold;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
}
</style>
